import { useEffect, useState, useCallback, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronDown, ChevronUp, Info, Plus, Trash2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useTranslation } from 'react-i18next';
import { suppliersService, Supplier, SupplierServiceWindow } from '../../services/suppliersService';
import { useWarehouseId } from '../../contexts/WarehouseContext';

interface SupplierDetailSidebarProps {
  supplierId: number | null;
  initialSupplierName?: string | null;
  isOpen: boolean;
  onClose: () => void;
  onFrequencyUpdated?: () => void;
  onFrequencyUpdating?: (supplierId: number) => void;
  onLeadTimeUpdated?: () => void;
  onLeadTimeUpdating?: (supplierId: number) => void;
  onCalendarUpdated?: () => void;
  onIdealCoverageUpdated?: () => void;
}

interface SupplierStats {
  monthly_data: Array<{
    month: string;
    month_name: string;
    year: number;
    month_number: number;
    median_lead_time: number;
    orders_count: number;
  }>;
  median_lead_time_observed: number | null;
  supplier: {
    id: number;
    code: string;
    name: string;
    lead_time: number;
  };
  last_purchase_date: string | null;
}

type ServiceWindowDraft = {
  localId: string;
  serviceDay: number | '';
  deliveryDay: number | '';
  isActive: boolean;
};

const WEEKDAY_VALUES = [1, 2, 3, 4, 5, 6, 7] as const;

function newWindowLocalId(): string {
  return `w-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function windowsFromSupplier(supplier: Supplier | null): ServiceWindowDraft[] {
  if (!supplier) return [];
  const raw = (supplier.serviceWindows ?? (supplier as { service_windows?: SupplierServiceWindow[] }).service_windows ?? []) as SupplierServiceWindow[];
  if (raw.length) {
    return raw.map((window) => ({
      localId: String(window.id ?? newWindowLocalId()),
      serviceDay: (window.serviceDay ?? window.service_day ?? '') as number | '',
      deliveryDay: (window.deliveryDay ?? window.delivery_day ?? '') as number | '',
      isActive: window.isActive ?? window.is_active ?? true,
    }));
  }
  const legacy = supplier.serviceDay ?? (supplier as { service_day?: number | null }).service_day ?? null;
  if (legacy) {
    return [{ localId: newWindowLocalId(), serviceDay: legacy, deliveryDay: '', isActive: true }];
  }
  return [];
}

export function SupplierDetailSidebar({
  supplierId,
  initialSupplierName,
  isOpen,
  onClose,
  onFrequencyUpdated,
  onFrequencyUpdating,
  onLeadTimeUpdated,
  onLeadTimeUpdating,
  onCalendarUpdated,
  onIdealCoverageUpdated,
}: SupplierDetailSidebarProps) {
  const { t, i18n } = useTranslation();
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsData, setStatsData] = useState<SupplierStats | null>(null);
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [purchaseFrequency, setPurchaseFrequency] = useState<number | null>(null);
  const [updatingFrequency, setUpdatingFrequency] = useState(false);
  const [originalFrequency, setOriginalFrequency] = useState<number | null>(null);
  const [staticLeadTime, setStaticLeadTime] = useState<number | null>(null);
  const [originalStaticLeadTime, setOriginalStaticLeadTime] = useState<number | null>(null);
  const [leadTimeIsStatic, setLeadTimeIsStatic] = useState(false);
  const [updatingLeadTime, setUpdatingLeadTime] = useState(false);
  const [nextManagementDate, setNextManagementDate] = useState<string | null>(null);
  const [managementComment, setManagementComment] = useState<string>('');
  const [originalManagementDate, setOriginalManagementDate] = useState<string | null>(null);
  const [originalManagementComment, setOriginalManagementComment] = useState<string>('');
  const [updatingDate, setUpdatingDate] = useState(false);
  const [updatingComment, setUpdatingComment] = useState(false);
  const [isManagementOpen, setIsManagementOpen] = useState(false);
  const [calendarWindows, setCalendarWindows] = useState<ServiceWindowDraft[]>([]);
  const [useCalendarLeadTime, setUseCalendarLeadTime] = useState(false);
  const [updatingCalendar, setUpdatingCalendar] = useState(false);
  const [idealCoverageDays, setIdealCoverageDays] = useState<number | null>(null);
  const [originalIdealCoverageDays, setOriginalIdealCoverageDays] = useState<number | null>(null);
  const [updatingIdealCoverage, setUpdatingIdealCoverage] = useState(false);
  const warehouseId = useWarehouseId();
  // Ref para evitar llamadas duplicadas cuando se presiona Enter
  const isProcessingUpdateRef = useRef(false);
  const lastUpdateValueRef = useRef<number | null>(null);
  const isProcessingLeadTimeRef = useRef(false);
  const lastLeadTimeValueRef = useRef<number | null>(null);
  const isProcessingIdealCoverageRef = useRef(false);

  const loadSupplierData = useCallback(async () => {
    if (!supplierId || typeof warehouseId !== 'number') return;
    
    setSupplierLoading(true);
    setStatsLoading(true);
    setStatsData(null);
    try {
      const [supplierData, statsPayload] = await Promise.all([
        suppliersService.getSupplier(supplierId.toString()),
        suppliersService.getSupplierStats(supplierId, warehouseId),
      ]);

      setSupplier(supplierData);
      setStatsData(statsPayload as any);
      setCalendarWindows(windowsFromSupplier(supplierData));
      setUseCalendarLeadTime(Boolean(
        supplierData?.useCalendarLeadTime ?? (supplierData as { use_calendar_lead_time?: boolean } | null)?.use_calendar_lead_time
      ));

      const newFrequency = supplierData?.maxPurchaseFrequency || null;
      setPurchaseFrequency(newFrequency);
      setOriginalFrequency(newFrequency);
      const fixedLt =
        supplierData?.leadTime ??
        (supplierData as { lead_time?: number } | null)?.lead_time ??
        statsPayload?.supplier?.lead_time ??
        null;
      const fixedLtNum = fixedLt == null ? null : Number(fixedLt);
      setStaticLeadTime(fixedLtNum);
      setOriginalStaticLeadTime(fixedLtNum);
      setLeadTimeIsStatic(Boolean(
        supplierData?.leadTimeIsStatic
        ?? (supplierData as { lead_time_is_static?: boolean } | null)?.lead_time_is_static
      ));
      lastLeadTimeValueRef.current = fixedLtNum;
      isProcessingLeadTimeRef.current = false;
      const managementDate = supplierData?.nextManagementDate || null;
      const managementCommentValue = supplierData?.managementComment || '';
      setNextManagementDate(managementDate);
      setManagementComment(managementCommentValue);
      setOriginalManagementDate(managementDate);
      setOriginalManagementComment(managementCommentValue);
      const coverageRaw =
        supplierData?.idealCoverageDays
        ?? (supplierData as { ideal_coverage_days?: number | null } | null)?.ideal_coverage_days
        ?? null;
      const coverageNum = coverageRaw == null ? null : Number(coverageRaw);
      const coverageValue =
        coverageNum != null && Number.isFinite(coverageNum) && coverageNum >= 1
          ? Math.trunc(coverageNum)
          : null;
      setIdealCoverageDays(coverageValue);
      setOriginalIdealCoverageDays(coverageValue);
      isProcessingIdealCoverageRef.current = false;
      isProcessingUpdateRef.current = false;
      lastUpdateValueRef.current = newFrequency;
    } catch (error) {
      console.error('Error loading supplier data:', error);
      setStatsData(null);
    } finally {
      setSupplierLoading(false);
      setStatsLoading(false);
    }
  }, [supplierId, warehouseId]);

  const handleUpdateFrequency = useCallback(async (newFrequency: number) => {
    if (!supplierId || !newFrequency || newFrequency < 1) return;
    
    // Evitar llamadas duplicadas: si ya se est? procesando o es el mismo valor que la ?ltima actualizaci?n
    if (isProcessingUpdateRef.current) {
      return;
    }
    
    if (lastUpdateValueRef.current === newFrequency) {
      return;
    }
    
    isProcessingUpdateRef.current = true;
    lastUpdateValueRef.current = newFrequency;
    setUpdatingFrequency(true);
    const previousFrequency = purchaseFrequency;
    
    // Notificar al padre que se est? actualizando para mostrar loading en la tabla
    if (onFrequencyUpdating && supplierId) {
      onFrequencyUpdating(supplierId);
    }
    
    // Optimistic update
    setPurchaseFrequency(newFrequency);
    
    try {
      const response = await suppliersService.updatePurchaseFrequency(supplierId, newFrequency);
      
      if (response.success) {
        // Recargar datos del sidebar
        await loadSupplierData();
        if (onFrequencyUpdated) {
          onFrequencyUpdated();
        }
      } else {
        // Revertir
        setPurchaseFrequency(previousFrequency);
        lastUpdateValueRef.current = previousFrequency;
        alert(t('supply.supplierDetail.errorUpdatingFrequency'));
      }
    } catch (error) {
      console.error('Error updating frequency:', error);
      setPurchaseFrequency(previousFrequency);
      lastUpdateValueRef.current = previousFrequency;
      alert(t('supply.supplierDetail.errorUpdatingFrequency'));
    } finally {
      setUpdatingFrequency(false);
      isProcessingUpdateRef.current = false;
    }
  }, [supplierId, purchaseFrequency, loadSupplierData, onFrequencyUpdated, onFrequencyUpdating, t]);

  const handleUpdateLeadTime = useCallback(async (newLeadTime: number, options?: { force?: boolean }) => {
    if (!supplierId || newLeadTime < 0) return;
    if (isProcessingLeadTimeRef.current) return;
    if (!options?.force && lastLeadTimeValueRef.current === newLeadTime) return;

    isProcessingLeadTimeRef.current = true;
    lastLeadTimeValueRef.current = newLeadTime;
    setUpdatingLeadTime(true);
    const previous = staticLeadTime;
    const previousStatic = leadTimeIsStatic;

    if (onLeadTimeUpdating && supplierId) {
      onLeadTimeUpdating(supplierId);
    }
    setStaticLeadTime(newLeadTime);
    setLeadTimeIsStatic(true);

    try {
      const response = await suppliersService.updateSupplierLeadTime(supplierId, newLeadTime);
      if (response.success) {
        await loadSupplierData();
        if (onLeadTimeUpdated) {
          onLeadTimeUpdated();
        }
      } else {
        setStaticLeadTime(previous);
        setLeadTimeIsStatic(previousStatic);
        lastLeadTimeValueRef.current = previous;
        alert(t('supply.supplierDetail.errorUpdatingLeadTime'));
      }
    } catch (error) {
      console.error('Error updating supplier lead time:', error);
      setStaticLeadTime(previous);
      setLeadTimeIsStatic(previousStatic);
      lastLeadTimeValueRef.current = previous;
      alert(t('supply.supplierDetail.errorUpdatingLeadTime'));
    } finally {
      setUpdatingLeadTime(false);
      isProcessingLeadTimeRef.current = false;
    }
  }, [supplierId, staticLeadTime, leadTimeIsStatic, loadSupplierData, onLeadTimeUpdated, onLeadTimeUpdating, t]);

  const handleClearSupplierStaticLeadTime = useCallback(async () => {
    if (!supplierId) return;
    if (isProcessingLeadTimeRef.current) return;

    isProcessingLeadTimeRef.current = true;
    setUpdatingLeadTime(true);
    const previous = staticLeadTime;
    const previousStatic = leadTimeIsStatic;

    if (onLeadTimeUpdating && supplierId) {
      onLeadTimeUpdating(supplierId);
    }
    setLeadTimeIsStatic(false);

    try {
      const response = await suppliersService.clearSupplierStaticLeadTime(supplierId);
      if (response.success) {
        await loadSupplierData();
        if (onLeadTimeUpdated) {
          onLeadTimeUpdated();
        }
      } else {
        setLeadTimeIsStatic(previousStatic);
        setStaticLeadTime(previous);
        alert(t('supply.supplierDetail.errorUpdatingLeadTime'));
      }
    } catch (error) {
      console.error('Error clearing supplier static lead time:', error);
      setLeadTimeIsStatic(previousStatic);
      setStaticLeadTime(previous);
      alert(t('supply.supplierDetail.errorUpdatingLeadTime'));
    } finally {
      setUpdatingLeadTime(false);
      isProcessingLeadTimeRef.current = false;
    }
  }, [supplierId, staticLeadTime, leadTimeIsStatic, loadSupplierData, onLeadTimeUpdated, onLeadTimeUpdating, t]);

  const handleUpdateIdealCoverage = useCallback(async (nextValue: number | null) => {
    if (!supplierId) return;
    if (isProcessingIdealCoverageRef.current) return;
    if (nextValue !== null && (Number.isNaN(nextValue) || nextValue < 1)) return;
    if (nextValue === originalIdealCoverageDays) return;

    isProcessingIdealCoverageRef.current = true;
    setUpdatingIdealCoverage(true);
    const previous = idealCoverageDays;
    setIdealCoverageDays(nextValue);

    try {
      const response = await suppliersService.updateSupplierIdealCoverage(supplierId, nextValue);
      if (response.success) {
        const saved =
          response.supplier?.idealCoverageDays
          ?? (response.supplier as { ideal_coverage_days?: number | null } | undefined)?.ideal_coverage_days
          ?? nextValue;
        const normalized =
          saved == null || Number(saved) < 1 ? null : Math.trunc(Number(saved));
        setIdealCoverageDays(normalized);
        setOriginalIdealCoverageDays(normalized);
        onIdealCoverageUpdated?.();
      } else {
        setIdealCoverageDays(previous);
        alert(t('supply.supplierDetail.errorUpdatingIdealCoverage'));
      }
    } catch (error) {
      console.error('Error updating supplier ideal coverage:', error);
      setIdealCoverageDays(previous);
      alert(t('supply.supplierDetail.errorUpdatingIdealCoverage'));
    } finally {
      setUpdatingIdealCoverage(false);
      isProcessingIdealCoverageRef.current = false;
    }
  }, [
    supplierId,
    idealCoverageDays,
    originalIdealCoverageDays,
    onIdealCoverageUpdated,
    t,
  ]);

  const handleUpdateManagement = useCallback(async (field?: 'date' | 'comment') => {
    if (!supplierId) return;
    
    // Verificar si hay cambios
    const hasDateChanged = nextManagementDate !== originalManagementDate;
    const hasCommentChanged = managementComment !== originalManagementComment;
    
    if (!hasDateChanged && !hasCommentChanged) {
      return; // No hay cambios, no hacer nada
    }
    
    // Determinar qu? campo se est? actualizando
    const isUpdatingDate = field === 'date' || (hasDateChanged && !field);
    const isUpdatingComment = field === 'comment' || (hasCommentChanged && !field);
    
    if (isUpdatingDate) setUpdatingDate(true);
    if (isUpdatingComment) setUpdatingComment(true);
    
    try {
      const response = await suppliersService.updateSupplierManagement(
        supplierId,
        nextManagementDate || null,
        managementComment || ''
      );
      
      if (response.success) {
        // Actualizar valores originales sin recargar todo
        setOriginalManagementDate(nextManagementDate);
        setOriginalManagementComment(managementComment);
      } else {
        alert(t('supply.supplierDetail.errorUpdatingManagement') || 'Error al actualizar la gesti?n');
        // Revertir cambios en caso de error
        setNextManagementDate(originalManagementDate);
        setManagementComment(originalManagementComment);
      }
    } catch (error) {
      console.error('Error updating management:', error);
      alert(t('supply.supplierDetail.errorUpdatingManagement') || 'Error al actualizar la gesti?n');
      // Revertir cambios en caso de error
      setNextManagementDate(originalManagementDate);
      setManagementComment(originalManagementComment);
    } finally {
      setUpdatingDate(false);
      setUpdatingComment(false);
    }
  }, [supplierId, nextManagementDate, managementComment, originalManagementDate, originalManagementComment, t]);

  const handleSaveServiceCalendar = useCallback(async () => {
    if (!supplierId) return;

    const normalized = [];
    for (const window of calendarWindows) {
      if (window.serviceDay === '') {
        alert(t('supply.supplierDetail.serviceDayRequired'));
        return;
      }
      normalized.push({
        service_day: Number(window.serviceDay),
        delivery_day: window.deliveryDay === '' ? null : Number(window.deliveryDay),
        is_active: window.isActive,
      });
    }

    setUpdatingCalendar(true);
    try {
      const response = await suppliersService.updateServiceCalendar(supplierId, {
        windows: normalized,
        use_calendar_lead_time: useCalendarLeadTime,
      });
      if (!response.success) {
        alert(t('supply.supplierDetail.errorUpdatingCalendar'));
        return;
      }
      if (response.supplier) {
        setSupplier(response.supplier);
        setCalendarWindows(windowsFromSupplier(response.supplier));
        setUseCalendarLeadTime(Boolean(
          response.supplier.useCalendarLeadTime
          ?? (response.supplier as { use_calendar_lead_time?: boolean }).use_calendar_lead_time
        ));
      } else {
        await loadSupplierData();
      }
      onCalendarUpdated?.();
    } catch (error) {
      console.error('Error updating service calendar:', error);
      alert(t('supply.supplierDetail.errorUpdatingCalendar'));
    } finally {
      setUpdatingCalendar(false);
    }
  }, [supplierId, calendarWindows, useCalendarLeadTime, loadSupplierData, onCalendarUpdated, t]);

  // Manejar el montaje/desmontaje del componente
  useEffect(() => {
    if (isOpen && supplierId) {
      setIsMounted(true);
    } else if (!isOpen && isMounted) {
      const sidebar = document.querySelector('[data-supplier-sidebar]') as HTMLElement;
      if (sidebar) {
        void sidebar.offsetHeight;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(false);
          setTimeout(() => {
            setIsMounted(false);
            setStatsData(null);
            setSupplier(null);
            setPurchaseFrequency(null);
            setOriginalFrequency(null);
            setNextManagementDate(null);
            setManagementComment('');
            setOriginalManagementDate(null);
            setOriginalManagementComment('');
            setIsManagementOpen(false);
            setCalendarWindows([]);
            setUseCalendarLeadTime(false);
            setUpdatingCalendar(false);
            // Resetear refs cuando se cierra el sidebar
            isProcessingUpdateRef.current = false;
            lastUpdateValueRef.current = null;
          }, 300);
        });
      });
    }
  }, [isOpen, supplierId, isMounted]);

  // Controlar la animaci?n
  useLayoutEffect(() => {
    if (isMounted && isOpen && supplierId) {
      const sidebar = document.querySelector('[data-supplier-sidebar]');
      if (sidebar) {
        const timer = setTimeout(() => {
          setIsAnimating(true);
        }, 10);
        return () => clearTimeout(timer);
      }
    }
  }, [isMounted, isOpen, supplierId]);

  // Cargar datos cuando el sidebar est? abierto
  useEffect(() => {
    if (isOpen && supplierId && isMounted) {
      loadSupplierData();
    }
  }, [isOpen, supplierId, isMounted, loadSupplierData]);

  // Funci?n para calcular fecha sugerida de gesti?n
  const getSuggestedManagementDate = useCallback((): string | null => {
    if (!statsData?.last_purchase_date || !purchaseFrequency || purchaseFrequency <= 1) {
      return null;
    }
    
    const lastPurchase = new Date(statsData.last_purchase_date);
    const suggestedDate = new Date(lastPurchase);
    suggestedDate.setDate(suggestedDate.getDate() + purchaseFrequency);
    
    // Ajustar al service day si existe (1=lunes, 5=viernes)
    // Manejar tanto camelCase como snake_case por compatibilidad
    const serviceDay = supplier?.serviceDay ?? (supplier as any)?.service_day ?? null;
    
    if (serviceDay && serviceDay >= 1 && serviceDay <= 5) {
      const dayOfWeek = suggestedDate.getDay(); // 0=domingo, 1=lunes, ..., 6=s?bado
      const targetDay = serviceDay; // 1=lunes, 5=viernes
      // Convertir a formato 1-7 (lunes-domingo) para calcular diferencia
      const currentDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      const diff = targetDay - currentDay;
      suggestedDate.setDate(suggestedDate.getDate() + diff);
    }
    
    const result = suggestedDate.toISOString().split('T')[0];
    
    // Validar que la fecha sugerida no sea anterior a hoy
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const resultDate = new Date(result);
    resultDate.setHours(0, 0, 0, 0);
    
    if (resultDate < today) {
      return null;
    }
    
    return result;
  }, [statsData?.last_purchase_date, purchaseFrequency, supplier]);

  // Preparar datos para la gr?fica (solo hist?rico)
  const realLeadTimeKey = t('supply.supplierDetail.realLeadTime');
  const chartData: Array<{
    month: string;
    [key: string]: string | number | null;
  }> = statsData?.monthly_data.map((item) => {
    return {
      month: `${item.month_name.substring(0, 3)} ${item.year}`,
      [realLeadTimeKey]: item.median_lead_time
    };
  }) || [];

  // Calcular el valor m?ximo para el eje Y (solo hist?rico con margen)
  const allValues: number[] = [];
  chartData.forEach(item => {
    const value = item[realLeadTimeKey];
    if (value !== null && typeof value === 'number') {
      allValues.push(value);
    }
  });
  
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;
  // Calcular el dominio del eje Y con un margen del 20% superior
  const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.2) : undefined;

  if (!isMounted || !supplierId) return null;

  const sidebarContent = (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black z-[90] transition-opacity duration-300 ease-in-out ${
          isAnimating ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      
      {/* Sidebar */}
      <div
        data-supplier-sidebar
        className={`fixed right-0 inset-y-0 w-full max-w-2xl bg-white shadow-xl z-[100] transform transition-transform duration-300 ease-in-out ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        } overflow-y-auto`}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <div>
                {supplier?.name && (
                  <h2 className="text-2xl font-bold text-gray-900">
                    {supplier.name}
                  </h2>
                )}
                {!supplier?.name && initialSupplierName && (
                  <h2 className="text-2xl font-bold text-gray-900">
                    {initialSupplierName}
                  </h2>
                )}
                {supplier?.code && (
                  <p className="text-sm text-gray-500 mt-1">
                    {supplier.code}
                  </p>
                )}
                {statsData?.last_purchase_date && (
                  <p className="text-sm text-gray-500 mt-1">
                    {t('supply.supplierDetail.lastPurchase')}: {new Date(statsData.last_purchase_date).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : i18n.language === 'it' ? 'it-IT' : 'en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-6 w-6 text-gray-500" />
            </button>
          </div>

          {supplier ? (
            <>
              {/* Secci?n de Gesti?n del Proveedor - Acorde?n */}
              <div className="mb-6">
                <button
                  onClick={() => setIsManagementOpen(!isManagementOpen)}
                  className="w-full flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg p-2 -ml-2 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <h3 className="text-base font-medium text-gray-900">
                      {t('supply.supplierDetail.managementSection') || 'Gesti?n del Proveedor'}
                    </h3>
                    {nextManagementDate && (() => {
                      const managementDate = new Date(nextManagementDate);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      managementDate.setHours(0, 0, 0, 0);
                      // Solo mostrar si la fecha es posterior a hoy
                      if (managementDate > today) {
                        return (
                          <span className="text-xs text-gray-400 font-normal">
                            {t('supply.supplierDetail.nextManagementLabel') || 'Pr?xima gesti?n'}: {managementDate.toLocaleDateString(i18n.language === 'es' ? 'es-ES' : i18n.language === 'it' ? 'it-IT' : 'en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                  {isManagementOpen ? (
                    <ChevronUp className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  )}
                </button>
                
                {isManagementOpen && (
                  <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
                    {/* Fecha de pr?xima gesti?n */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        {t('supply.supplierDetail.nextManagementDate') || 'Fecha de pr?xima gesti?n'}
                        <div className="relative group">
                          <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                          <div className="absolute left-0 top-full mt-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[110]">
                            <p className="leading-relaxed">
                              {t('supply.supplierDetail.nextManagementDateHelp') || 'Si est? establecida y es menor o igual a hoy, el proveedor no aparecer? en indicadores hasta que se actualice.'}
                            </p>
                            <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-900"></div>
                          </div>
                        </div>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={nextManagementDate || ''}
                          onChange={(e) => setNextManagementDate(e.target.value || null)}
                          onBlur={() => handleUpdateManagement('date')}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                        {(() => {
                          const hasNoDate = !nextManagementDate;
                          const suggestedDate = getSuggestedManagementDate();
                          if (!hasNoDate || !suggestedDate) return null;
                          
                          const suggestedDateFormatted = new Date(suggestedDate).toLocaleDateString(
                            i18n.language === 'es' ? 'es-ES' : i18n.language === 'it' ? 'it-IT' : 'en-US',
                            { year: 'numeric', month: 'short', day: 'numeric' }
                          );
                          
                          return (
                            <button
                              onClick={() => {
                                const suggested = getSuggestedManagementDate();
                                if (suggested) {
                                  setNextManagementDate(suggested);
                                  handleUpdateManagement('date');
                                }
                              }}
                              className="px-3 py-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors whitespace-nowrap"
                            >
                              {t('supply.supplierDetail.suggestedDate', { date: suggestedDateFormatted }) || `Fecha sugerida: ${suggestedDateFormatted}`}
                            </button>
                          );
                        })()}
                        {updatingDate && (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                        )}
                      </div>
                    </div>
                    
                    {/* Comentario de gesti?n */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        {t('supply.supplierDetail.managementComment') || 'Comentario de gesti?n'}
                        {updatingComment && (
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400"></div>
                        )}
                      </label>
                      <textarea
                        value={managementComment}
                        onChange={(e) => setManagementComment(e.target.value)}
                        onBlur={() => handleUpdateManagement('comment')}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            // Revertir cambios al presionar Escape
                            setNextManagementDate(originalManagementDate);
                            setManagementComment(originalManagementComment);
                            e.currentTarget.blur();
                          }
                        }}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                        placeholder={t('supply.supplierDetail.managementCommentPlaceholder') || 'Agregar notas sobre la gesti?n realizada...'}
                      />
                    </div>

                    <div className="pt-4 border-t border-gray-200 space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">
                          {t('supply.supplierDetail.serviceCalendarTitle')}
                        </h4>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                          {t('supply.supplierDetail.serviceCalendarHelp')}
                        </p>
                      </div>

                      <div className="space-y-2">
                        {calendarWindows.map((window) => (
                          <div key={window.localId} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-end">
                            <label className="text-xs text-gray-600">
                              {t('supply.supplierDetail.serviceDay')}
                              <select
                                value={window.serviceDay}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? '' : Number(e.target.value);
                                  setCalendarWindows((prev) =>
                                    prev.map((item) =>
                                      item.localId === window.localId ? { ...item, serviceDay: value } : item
                                    )
                                  );
                                }}
                                className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                              >
                                <option value="">{t('supply.supplierDetail.selectDay')}</option>
                                {WEEKDAY_VALUES.map((day) => (
                                  <option key={`service-${window.localId}-${day}`} value={day}>
                                    {t(`supply.supplierDetail.weekdays.${day}`)}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="text-xs text-gray-600">
                              {t('supply.supplierDetail.deliveryDay')}
                              <select
                                value={window.deliveryDay}
                                onChange={(e) => {
                                  const value = e.target.value === '' ? '' : Number(e.target.value);
                                  setCalendarWindows((prev) =>
                                    prev.map((item) =>
                                      item.localId === window.localId ? { ...item, deliveryDay: value } : item
                                    )
                                  );
                                }}
                                className="mt-1 w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                              >
                                <option value="">{t('supply.supplierDetail.selectDay')}</option>
                                {WEEKDAY_VALUES.map((day) => (
                                  <option key={`delivery-${window.localId}-${day}`} value={day}>
                                    {t(`supply.supplierDetail.weekdays.${day}`)}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-600 pb-2">
                              <input
                                type="checkbox"
                                checked={window.isActive}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setCalendarWindows((prev) =>
                                    prev.map((item) =>
                                      item.localId === window.localId ? { ...item, isActive: checked } : item
                                    )
                                  );
                                }}
                                className="rounded border-gray-300 text-blue-600"
                              />
                              {t('supply.supplierDetail.windowActive')}
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setCalendarWindows((prev) => prev.filter((item) => item.localId !== window.localId));
                              }}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md mb-0.5"
                              aria-label={t('supply.supplierDetail.removeWindow')}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          setCalendarWindows((prev) => [
                            ...prev,
                            { localId: newWindowLocalId(), serviceDay: '', deliveryDay: '', isActive: true },
                          ]);
                        }}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1.5 rounded-md"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t('supply.supplierDetail.addWindow')}
                      </button>

                      <label className="flex items-start gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={useCalendarLeadTime}
                          onChange={(e) => setUseCalendarLeadTime(e.target.checked)}
                          className="mt-0.5 rounded border-gray-300 text-blue-600"
                        />
                        <span>
                          {t('supply.supplierDetail.useCalendarLeadTime')}
                          <span className="block text-xs text-gray-500 mt-1 leading-relaxed">
                            {t('supply.supplierDetail.useCalendarLeadTimeHelp')}
                          </span>
                        </span>
                      </label>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSaveServiceCalendar}
                          disabled={updatingCalendar}
                          className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {updatingCalendar
                            ? t('supply.supplierDetail.saving')
                            : t('supply.supplierDetail.saveCalendar')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Frecuencia de compra, cobertura ideal y Lead Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 max-w-5xl mx-auto">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2 text-center">{t('supply.supplierDetail.purchaseFrequency')}</p>
                  <div className="flex items-center justify-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      value={purchaseFrequency ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value);
                        if (value === null || (!isNaN(value) && value > 0)) {
                          setPurchaseFrequency(value);
                        }
                      }}
                      onFocus={() => {
                        setOriginalFrequency(purchaseFrequency);
                      }}
                      onBlur={() => {
                        // Evitar procesar blur si ya se proces? Enter
                        if (isProcessingUpdateRef.current) {
                          return;
                        }
                        
                        const currentValue = purchaseFrequency;
                        if (!currentValue || currentValue < 1) {
                          setPurchaseFrequency(originalFrequency);
                        } else if (currentValue !== originalFrequency) {
                          handleUpdateFrequency(currentValue);
                        }
                        setOriginalFrequency(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // Prevenir el comportamiento por defecto
                          const value = e.currentTarget.value === '' ? null : parseInt(e.currentTarget.value);
                          if (!value || value < 1) {
                            setPurchaseFrequency(originalFrequency);
                            e.currentTarget.blur();
                          } else if (value !== originalFrequency) {
                            handleUpdateFrequency(value);
                            // No hacer blur inmediatamente, dejar que handleUpdateFrequency termine
                            setTimeout(() => {
                              e.currentTarget.blur();
                            }, 100);
                          } else {
                            e.currentTarget.blur();
                          }
                        } else if (e.key === 'Escape') {
                          setPurchaseFrequency(originalFrequency);
                          e.currentTarget.blur();
                        }
                      }}
                      disabled={updatingFrequency || supplierLoading}
                      className="w-24 px-2 py-1.5 text-base font-semibold border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    />
                    <span className="text-sm text-gray-500">{t('supply.supplierDetail.days')}</span>
                    {updatingFrequency && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2 text-center">
                    {t('supply.supplierDetail.idealCoverage')}
                  </p>
                  <div className="flex items-center justify-center space-x-2">
                    <input
                      type="number"
                      min={1}
                      value={idealCoverageDays ?? ''}
                      placeholder={t('supply.supplierDetail.idealCoveragePlaceholder')}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        if (value === null || (!Number.isNaN(value) && value >= 1)) {
                          setIdealCoverageDays(value);
                        }
                      }}
                      onFocus={() => setOriginalIdealCoverageDays(idealCoverageDays)}
                      onBlur={() => {
                        if (isProcessingIdealCoverageRef.current) return;
                        const currentValue = idealCoverageDays;
                        if (currentValue !== null && currentValue < 1) {
                          setIdealCoverageDays(originalIdealCoverageDays);
                        } else if (currentValue !== originalIdealCoverageDays) {
                          void handleUpdateIdealCoverage(currentValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = e.currentTarget.value === '' ? null : parseInt(e.currentTarget.value, 10);
                          if (value !== null && (Number.isNaN(value) || value < 1)) {
                            setIdealCoverageDays(originalIdealCoverageDays);
                            e.currentTarget.blur();
                          } else if (value !== originalIdealCoverageDays) {
                            void handleUpdateIdealCoverage(value);
                            setTimeout(() => e.currentTarget.blur(), 100);
                          } else {
                            e.currentTarget.blur();
                          }
                        } else if (e.key === 'Escape') {
                          setIdealCoverageDays(originalIdealCoverageDays);
                          e.currentTarget.blur();
                        }
                      }}
                      disabled={updatingIdealCoverage || supplierLoading}
                      className="w-24 px-2 py-1.5 text-base font-semibold border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-center"
                    />
                    <span className="text-sm text-gray-500">{t('supply.supplierDetail.days')}</span>
                    {updatingIdealCoverage && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-500 text-center leading-relaxed">
                    {t('supply.supplierDetail.idealCoverageHelp')}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1 text-center">{t('supply.supplierDetail.medianLeadTime3m')}</p>
                  <p className="text-2xl font-bold text-gray-900 text-center">
                    {statsData?.median_lead_time_observed !== null && statsData?.median_lead_time_observed !== undefined
                      ? `${statsData.median_lead_time_observed.toFixed(1)} ${t('supply.supplierDetail.days')}`
                      : t('supply.supplierDetail.notAvailable')}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1 text-center">{t('supply.supplierDetail.staticLeadTime')}</p>
                  <div className="flex items-center justify-center space-x-2">
                    <input
                      type="number"
                      min={0}
                      value={staticLeadTime ?? ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        setStaticLeadTime(Number.isNaN(value as number) ? null : value);
                      }}
                      onFocus={() => setOriginalStaticLeadTime(staticLeadTime)}
                      onBlur={() => {
                        const currentValue = staticLeadTime;
                        if (currentValue === null || currentValue < 0) {
                          setStaticLeadTime(originalStaticLeadTime);
                        } else if (currentValue !== originalStaticLeadTime) {
                          void handleUpdateLeadTime(currentValue);
                        }
                        setOriginalStaticLeadTime(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = e.currentTarget.value === '' ? null : parseInt(e.currentTarget.value, 10);
                          if (value === null || Number.isNaN(value) || value < 0) {
                            setStaticLeadTime(originalStaticLeadTime);
                            e.currentTarget.blur();
                          } else if (value !== originalStaticLeadTime) {
                            void handleUpdateLeadTime(value);
                            setTimeout(() => e.currentTarget.blur(), 100);
                          } else {
                            e.currentTarget.blur();
                          }
                        } else if (e.key === 'Escape') {
                          setStaticLeadTime(originalStaticLeadTime);
                          e.currentTarget.blur();
                        }
                      }}
                      disabled={updatingLeadTime || supplierLoading}
                      className="w-24 px-2 py-1.5 text-base font-semibold border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 text-center"
                    />
                    <span className="text-sm text-gray-500">{t('supply.supplierDetail.days')}</span>
                    {updatingLeadTime && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    )}
                  </div>
                  <label className="mt-3 flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={leadTimeIsStatic}
                      onChange={(e) => {
                        if (e.target.checked) {
                          if (staticLeadTime === null || staticLeadTime < 0) {
                            return;
                          }
                          void handleUpdateLeadTime(staticLeadTime, { force: true });
                        } else {
                          void handleClearSupplierStaticLeadTime();
                        }
                      }}
                      disabled={updatingLeadTime || supplierLoading}
                      className="mt-0.5 rounded border-gray-300 text-blue-600"
                    />
                    <span>
                      {t('supply.supplierDetail.staticLeadTimeToggle')}
                      <span className="block text-xs text-gray-500 mt-1 leading-relaxed">
                        {t('supply.supplierDetail.staticLeadTimeHelp')}
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Gr?fica */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">{t('supply.supplierDetail.leadTimeHistory')}</h3>
                {statsLoading ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]"></div>
                  </div>
                ) : chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis 
                        label={{ value: t('supply.supplierDetail.daysLabel'), angle: -90, position: 'insideLeft' }}
                        domain={yAxisMax ? [0, yAxisMax] : [0, 'auto']}
                      />
                      <Tooltip />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey={realLeadTimeKey} 
                        stroke="#1681ba" 
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        name={t('supply.supplierDetail.realLeadTime')}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-500">
                    {t('supply.supplierDetail.noDataAvailable')}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]"></div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(sidebarContent, document.body);
}

